import { useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { AlfredBulkUploadStep } from "@/components/alfred/bulk-import/alfred-bulk-upload-step"
import { AlfredBulkPreviewStep } from "@/components/alfred/bulk-import/alfred-bulk-preview-step"
import { AlfredBulkImportStep } from "@/components/alfred/bulk-import/alfred-bulk-import-step"
import { useSmithersBulkImportJobContext } from "@/components/smithers/bulk-import/smithers-bulk-import-job-context"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { ApiErrorState, ClientNotFoundState } from "@/components/shared/empty-states"
import { Button } from "@/components/ui/button"
import { Stepper } from "@/components/ui/stepper"
import { useSmithersClients } from "@/hooks/use-smithers-clients"
import { useToast } from "@/hooks/use-toast"
import { validateSmithersBulkImportFile } from "@/lib/smithers-bulk-import"
import { getSmithersDeviceListOrigin } from "@/lib/smithers-device-navigation"
import { smithersDeviceService } from "@/services/smithers-device-service"
import type { SmithersBulkImportSelection, SmithersBulkImportUploadResult } from "@/types/smithers-bulk-import"

const STEPS = [{ id: "upload", title: "Upload" }, { id: "preview", title: "Preview" }, { id: "import", title: "Import" }]

export function SmithersBulkAddPage() {
  const [params] = useSearchParams()
  const clientId = params.get("clientId")
  return <SmithersBulkAddFlow key={clientId ?? "all"} clientId={clientId} />
}

function SmithersBulkAddFlow({ clientId }: { clientId: string | null }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [origin] = useState(() => getSmithersDeviceListOrigin(location.state))
  const clients = useSmithersClients()
  const scopedClient = clients.data?.find((client) => client.clientId === clientId)
  const validClientIds = useMemo(() => new Set(clients.data?.map((client) => client.clientId) ?? []), [clients.data])
  const { startJob } = useSmithersBulkImportJobContext()
  const { toast } = useToast()
  const [step, setStep] = useState(0)
  const [selection, setSelection] = useState<SmithersBulkImportSelection | null>(null)
  const [result, setResult] = useState<SmithersBulkImportUploadResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitting = useRef(false)
  const devicesPath = `/smithers/devices${clientId === null ? "" : `?${new URLSearchParams({ clientId })}`}`

  const submitImport = async () => {
    if (!selection?.validation.isValid || submitting.current) return
    submitting.current = true
    setStep(2); setIsSubmitting(true); setError(null); setResult(null)
    try {
      const response = await smithersDeviceService.bulkCreate(selection.file)
      startJob(response)
      setResult(response)
      toast({ variant: "success", title: "Smithers bulk import started", description: `${response.totalRows} rows queued for background processing.` })
    } catch (error) { setError(error instanceof Error ? error.message : "Unable to upload the CSV file") }
    finally { submitting.current = false; setIsSubmitting(false) }
  }

  return <>
    <DashboardHeader />
    <main className="container mx-auto flex flex-col items-start gap-4 px-4 py-6">
      <Button variant="ghost" size="sm" asChild><Link to={devicesPath} onClick={(event) => {
        if (isSubmitting) event.preventDefault()
        else if (origin && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) { event.preventDefault(); navigate(devicesPath, { replace: true, state: { smithersDeviceList: origin } }) }
      }} aria-disabled={isSubmitting}>Back to Devices</Link></Button>
      <h1 className="text-2xl font-semibold text-foreground">{clientId === null ? "Bulk add Smithers devices" : `Bulk add Smithers devices to ${clientId}`}</h1>
      <p className="text-sm text-muted-foreground">Upload a CSV to validate devices before starting the import. Clients must already exist. Replace the sample client IDs with your client codes.</p>
      {clientId !== null && <p className="text-sm text-muted-foreground">This page only accepts devices for {clientId}. For multiple clients, use the <Link to="/smithers/devices/bulk-add" className="underline underline-offset-4">general bulk add page</Link>.</p>}
      <p className="text-sm text-muted-foreground">Blank optional cells use global defaults. Every row must set requiresProVenueInstance to TRUE or FALSE:</p>
      <ul className="list-disc pl-5 text-sm text-muted-foreground">
        <li>TRUE with a blank proVenueInstanceId uses the global default ProVenue instance.</li>
        <li>TRUE with a proVenueInstanceId uses that ProVenue instance.</li>
        <li>FALSE attaches no ProVenue instance. Leave proVenueInstanceId blank; any value is ignored.</li>
      </ul>
      <p className="text-sm text-muted-foreground">Instance IDs reference shared records; credentials are not included.</p>
      <Stepper steps={STEPS} currentStep={step === 2 && result ? STEPS.length : step} className="w-full max-w-3xl py-2" />
      {clients.isLoading ? <p role="status">Loading clients…</p> : clients.error ? <ApiErrorState onRetry={() => void clients.mutate()} /> : clientId !== null && !scopedClient ? <ClientNotFoundState onAction={() => navigate("/smithers/clients")} /> : <>
        {step === 0 && <AlfredBulkUploadStep productName="Smithers" templateFileName="smithers_bulk_import_template.csv" validateFile={validateSmithersBulkImportFile} requiredClientId={scopedClient?.clientId} validClientIds={validClientIds} selection={selection} onSelectionChange={setSelection} onContinue={() => setStep(1)} />}
        {step === 1 && selection && <AlfredBulkPreviewStep selection={selection} onBack={() => setStep(0)} onConfirm={() => void submitImport()} />}
        {step === 2 && selection && <AlfredBulkImportStep productName="Smithers" fileName={selection.file.name} isSubmitting={isSubmitting} result={result} errorMessage={error} onBack={() => { setError(null); setStep(1) }} onRetry={() => void submitImport()} />}
      </>}
    </main>
  </>
}
